import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import Stepper from '../../../components/Stepper/Stepper';
import { Button } from '../../../components/EntityList';
import { toast } from '../../../toasts';
import { getError } from '../../../utils';
import { useLabels } from '../../../hooks/useLabels';
import { BASE_LABELS } from '../../../constants/labels';
import importService from '../../../services/import.service';
import { partsToIso, sheetToTable } from './importFile';
import {
	autoMap,
	columnFallback,
	fieldCandidates,
	guessLookup,
	loadPicks,
	loadSavedMapping,
	pickKey,
	savePicks,
	saveMapping,
} from './autoMap';
import StepFile from './StepFile';
import StepMapping from './StepMapping';
import StepReview from './StepReview';
import StepResult from './StepResult';

const STEP_KEYS = ['import.step.file', 'import.step.mapping', 'import.step.review', 'import.step.result'];
const STEP = { FILE: 0, COLUMNS: 1, REVIEW: 2, RESULT: 3 };

/**
 * Excel importer wizard: Archivo -> Columnas (columns, how each reference is found, options) -> Revisión -> Resultado.
 * All state lives here, so moving back and forth keeps the file, mapping and
 * links. The browser parses the file; the server validates and commits JSON rows.
 */
export default function ImportTool() {
	const { t } = useLabels();
	const [schemas, setSchemas] = useState(null);
	const [entityKey, setEntityKey] = useState('bill');
	const [file, setFile] = useState(null); // { name, workbook }
	const [sheetName, setSheetName] = useState('');
	const [mapping, setMapping] = useState({});
	const [links, setLinks] = useState({});
	const [options, setOptions] = useState(() => ({
		defaultAccountType: 'Persona',
		notify: false,
		registerPayment: true,
	}));
	// Data completed in the review: { [rowNumber]: { values, ids, labels, parts } }; labels are only for
	// display, parts holds dates typed as { day, month, year }
	const [fills, setFills] = useState({});
	// Records picked for file values that matched nothing (see loadPicks); persisted per entity
	const [picks, setPicks] = useState({});
	const [dirty, setDirty] = useState(false); // fills/picks not yet re-validated
	const [step, setStep] = useState(0);
	const [validation, setValidation] = useState(null);
	const [validating, setValidating] = useState(false);
	const [previewVersion, setPreviewVersion] = useState(0);
	const [progress, setProgress] = useState(null); // { done, total } while committing
	const [result, setResult] = useState(null);

	useEffect(() => {
		importService
			.getSchemas()
			.then(({ data }) => setSchemas(data))
			.catch((err) => toast.error(getError(err)));
	}, []);

	const schema = schemas?.entities.find((e) => e.key === entityKey) || null;

	useEffect(() => {
		if (schema) setPicks(loadPicks(schema.key));
	}, [schema?.key]);

	const table = useMemo(() => (file && sheetName ? sheetToTable(file.workbook, sheetName) : null), [file, sheetName]);
	const headers = useMemo(() => table?.headers || [], [table]);

	const referenceOf = useCallback(
		(field) => (field.type === 'reference' ? schema?.references.find((r) => r.key === field.reference) : null),
		[schema],
	);

	const runAutoMap = useCallback(
		({ useSaved }) => {
			if (!schema) return;
			const saved = useSaved ? loadSavedMapping(schema.key, headers) : {};
			const savedHeaders = new Set(Object.values(saved));
			const remainingFields = schema.fields.filter((f) => !saved[f.key]);
			const auto = autoMap(
				headers.filter((h) => !savedHeaders.has(h)),
				remainingFields,
				(field) => fieldCandidates(field, referenceOf(field), t, BASE_LABELS),
			);
			setMapping({ ...auto, ...saved });
		},
		[schema, headers, referenceOf, t],
	);

	// New entity or new set of columns: start from the saved mapping + auto-mapping
	useEffect(() => {
		runAutoMap({ useSaved: true });
	}, [schema?.key, headers.join('\u0001')]);

	// Fillable fields can be completed in the review, so only the others block this step
	const missingRequired = schema ? schema.fields.filter((f) => f.required && !f.fillable && !mapping[f.key]) : [];
	const fillableUnmapped = schema
		? schema.fields.filter((f) => f.required && f.fillable && !mapping[f.key] && !columnFallback(f, mapping))
		: [];

	// Default links from the mapped headers, recomputed as columns are assigned; the user's
	// choice is kept while its column is unchanged
	useEffect(() => {
		if (!schema) return;
		setLinks((prev) => {
			const next = { ...prev };
			schema.references.forEach((reference) => {
				const header = mapping[reference.key];
				if (!prev[reference.key] || prev[reference.key].header !== header) {
					next[reference.key] = { header, by: guessLookup(header, reference, t, BASE_LABELS) };
				}
			});
			if (schema.dueStrategies) {
				const strategy = prev.due?.strategy || (mapping.dueRef && !mapping.period ? 'customId' : schema.defaultDueStrategy);
				const monthSource = mapping.period ? prev.due?.monthSource || 'period' : 'date';
				next.due = { strategy, monthSource };
			}
			if (schema.matchOptions) {
				const mappedMatch = schema.matchOptions.map((o) => o.key).filter((key) => mapping[key]);
				next.match = prev.match ? prev.match.filter((key) => mapping[key]) : mappedMatch;
			}
			return next;
		});
	}, [schema, mapping]);

	// Rows as sent to the server: mapped cells, then remembered picks for their values, then what was completed in the review
	const buildRows = useCallback(
		() =>
			(table?.rows || []).map((row) => {
				const values = Object.fromEntries(Object.entries(mapping).map(([fieldKey, header]) => [fieldKey, row.cells[header]]));
				const ids = {};
				(schema?.references || []).forEach((reference) => {
					const cell = values[reference.key];
					const picked = cell !== undefined && cell !== '' ? picks[reference.key]?.[pickKey(cell)] : null;
					if (picked) ids[reference.key] = picked.id;
				});
				const fill = fills[row.rowNumber];
				const dates = {};
				Object.entries(fill?.parts || {}).forEach(([fieldKey, parts]) => {
					const iso = partsToIso(parts);
					if (iso) dates[fieldKey] = iso;
				});
				return {
					rowNumber: row.rowNumber,
					values: { ...values, ...fill?.values, ...dates },
					ids: { ...ids, ...fill?.ids },
				};
			}),
		[table, mapping, schema, picks, fills],
	);

	// patch: { values?, ids?, labels?, parts? } merged into each row's fill; an empty value clears it
	const fillRows = (rowNumbers, patch) => {
		setFills((prev) => {
			const next = { ...prev };
			rowNumbers.forEach((rowNumber) => {
				const current = next[rowNumber] || {};
				const merged = {};
				['values', 'ids', 'labels', 'parts'].forEach((part) => {
					const combined = { ...current[part], ...patch[part] };
					Object.keys(combined).forEach((key) => {
						if (combined[key] === '' || combined[key] === null || combined[key] === undefined) delete combined[key];
					});
					merged[part] = combined;
				});
				next[rowNumber] = merged;
			});
			return next;
		});
		setDirty(true);
	};

	// option: { id, label } or null to forget the pick
	const pickValue = (fieldKey, value, option) => {
		setPicks((prev) => {
			const forField = { ...prev[fieldKey] };
			if (option) forField[pickKey(value)] = option;
			else delete forField[pickKey(value)];
			const next = { ...prev, [fieldKey]: forField };
			savePicks(schema.key, next);
			return next;
		});
		setDirty(true);
	};

	const validate = async () => {
		setValidating(true);
		try {
			saveMapping(schema.key, mapping);
			const { data } = await importService.validate(schema.key, { rows: buildRows(), links, options });
			setValidation(data);
			setDirty(false);
			setPreviewVersion((v) => v + 1);
			setStep(STEP.REVIEW);
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setValidating(false);
		}
	};

	// plan: { create: [{ rowNumber, allowDuplicate }], updates: [{ rowNumber, id, changes, expected }] }
	const commit = async (plan) => {
		const rowsByNumber = new Map(buildRows().map((row) => [row.rowNumber, row]));
		const items = [
			...plan.create.map((c) => ({ kind: 'create', ...rowsByNumber.get(c.rowNumber), allowDuplicate: c.allowDuplicate })),
			...plan.updates.map((u) => ({ kind: 'update', ...u })),
		];
		const chunkSize = schemas.chunkSize || 100;
		const totals = { created: 0, updated: 0, failed: 0, results: [], warningDetails: [] };
		setProgress({ done: 0, total: items.length });
		for (let i = 0; i < items.length; i += chunkSize) {
			const chunk = items.slice(i, i + chunkSize);
			const body = {
				create: chunk
					.filter((c) => c.kind === 'create')
					.map(({ rowNumber, values, ids, allowDuplicate }) => ({ rowNumber, values, ids, allowDuplicate })),
				updates: chunk.filter((c) => c.kind === 'update').map(({ rowNumber, id, changes, expected }) => ({ rowNumber, id, changes, expected })),
				links,
				options,
			};
			try {
				const { data } = await importService.commit(schema.key, body);
				totals.created += data.created;
				totals.updated += data.updated;
				totals.failed += data.failed;
				totals.results.push(...data.results);
				totals.warningDetails.push(...data.warningDetails);
			} catch (err) {
				const error = getError(err);
				chunk.forEach((c) => totals.results.push({ rowNumber: c.rowNumber, outcome: 'failed', error }));
				totals.failed += chunk.length;
			}
			setProgress({ done: Math.min(items.length, i + chunk.length), total: items.length });
		}
		totals.results.sort((a, b) => a.rowNumber - b.rowNumber);
		totals.status = totals.failed === 0 ? 'ok' : totals.created + totals.updated > 0 ? 'partial' : 'failed';
		setResult(totals);
		setProgress(null);
		setStep(STEP.RESULT);
		if (totals.status === 'ok') toast.success(`${t('import.result.ok')} ${totals.created + totals.updated}`);
		else if (totals.status === 'partial') toast.warning(t('import.result.partial'));
		else toast.error(t('import.result.failed'));
	};

	const startOver = () => {
		setFile(null);
		setSheetName('');
		setValidation(null);
		setResult(null);
		setFills({});
		setDirty(false);
		setStep(STEP.FILE);
	};

	if (!schemas) {
		return (
			<div className='tw:py-8 tw:text-center'>
				<Spinner animation='border' size='sm' />
			</div>
		);
	}

	const steps = STEP_KEYS.map((key, i) => ({ counter: i + 1, name: t(key) }));
	const tooManyRows = (table?.rows.length || 0) > schemas.maxRows;
	const canLeaveFile = !!table && table.rows.length > 0 && !tooManyRows;

	return (
		<div className='entity-list-page tw:space-y-5'>
			<Stepper steps={steps} activeStep={step + 1} />

			{step === STEP.FILE && (
				<StepFile
					entities={schemas.entities}
					entityKey={entityKey}
					onEntityChange={(key) => {
						setEntityKey(key);
						setLinks({});
						setFills({});
						setValidation(null);
					}}
					file={file}
					onFile={(next) => {
						setFile(next);
						setSheetName(next?.workbook.SheetNames[0] || '');
						setLinks({});
						setFills({});
						setValidation(null);
					}}
					sheetName={sheetName}
					onSheetChange={setSheetName}
					table={table}
					maxRows={schemas.maxRows}
				/>
			)}

			{step === STEP.COLUMNS && schema && (
				<StepMapping
					schema={schema}
					headers={headers}
					mapping={mapping}
					onMappingChange={setMapping}
					onAutoMap={() => runAutoMap({ useSaved: false })}
					missingRequired={missingRequired}
					fillableUnmapped={fillableUnmapped}
					links={links}
					onLinksChange={setLinks}
					options={options}
					onOptionsChange={setOptions}
				/>
			)}

			{step === STEP.REVIEW && schema && validation && (
				<StepReview
					key={previewVersion}
					schema={schema}
					validation={validation}
					table={table}
					mapping={mapping}
					progress={progress}
					onApply={commit}
					fills={fills}
					onFill={fillRows}
					picks={picks}
					onPick={pickValue}
					dirty={dirty}
					validating={validating}
					onRevalidate={validate}
				/>
			)}

			{step === STEP.RESULT && result && (
				<StepResult
					result={result}
					table={table}
					onReviewAgain={validate}
					onStartOver={startOver}
					validating={validating}
					fileName={file?.name}
				/>
			)}

			{step < STEP.REVIEW && (
				<div className='tw:flex tw:justify-between tw:border-t tw:border-border tw:pt-4'>
					<Button onClick={() => setStep((s) => s - 1)} disabled={step === STEP.FILE}>
						{t('import.back')}
					</Button>
					{step === STEP.FILE && (
						<Button variant='primary' onClick={() => setStep(STEP.COLUMNS)} disabled={!canLeaveFile}>
							{t('import.next')}
						</Button>
					)}
					{step === STEP.COLUMNS && (
						<Button
							variant='primary'
							onClick={validate}
							disabled={validating || missingRequired.length > 0 || Object.keys(mapping).length === 0}
						>
							{validating ? (
								<>
									<Spinner as='span' size='sm' animation='border' className='tw:mr-1' />
									{t('import.validating')}
								</>
							) : (
								t('import.validate')
							)}
						</Button>
					)}
				</div>
			)}
			{step === STEP.REVIEW && (
				<div className='tw:flex tw:justify-start'>
					<Button onClick={() => setStep(STEP.COLUMNS)} disabled={!!progress}>
						{t('import.back')}
					</Button>
				</div>
			)}
		</div>
	);
}
